import { makeStyles, Box, Paper, Grid } from '@material-ui/core'
import React from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import ContentfulImage from '@aletheia/common/components/ContentfulImage'
import {
  OrganizationType,
  useGetOrganizationsQueryWithReducer,
} from '@aletheia/graphql'

import { IFund } from '../../contentful'
import { SansSerifTypography } from '../../globals/theme'
import Section from './Section'

const fundDescriptionMap = {
  'global-development': {
    description: `Funding to outstanding, evidence-based opportunities to provide better access to healthcare and economic development where it's needed most.`,
    usually_supports: 'Established organizations',
    decides_using: 'Scientific evidence and fund managers’ judgment',
  },
  'animal-welfare': {
    description: `Funding to organizations and projects that will help alleviate the suffering of millions or billions of animals.`,
    usually_supports: 'Early-stage and established organizations',
    decides_using: 'Organizational track record and fund managers’ judgment',
  },
  'far-future': {
    description: `Funding to people or projects that aim to improve the long-term future, such as by reducing risks from artificial intelligence and engineered pandemics.`,
    usually_supports:
      'Individuals and organizations (includes speculative opportunities)',
    decides_using: 'Fund managers’ judgment',
  },
  'ea-community': {
    description: `Funding to organizations or people that aim to grow or improve the effective altruism community.`,
    usually_supports:
      'Organizations and early-stage projects (includes speculative opportunities)',
    decides_using: 'Fund managers’ judgment',
  },
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.light,
  },
  fundContentWrapper: { padding: theme.spacing(4) },
  fundName: {
    marginTop: 0,
  },
  fundDescription: {},
  fundNote: {
    color: theme.palette.grey[600],
  },
  fundWrapper: {
    marginBottom: theme.spacing(4),
    overflow: 'hidden',
  },
  fundImageWrapper: {
    alignSelf: 'stretch',
    '& > span': { height: '100%', width: '100%' },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxHeight: theme.spacing(20),
      marginBottom: theme.spacing(1),
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}))

export const OurFunds: React.FC<{ Funds: IFund[] }> = ({ Funds }) => {
  const classes = useStyles()
  const [Organizations] = useGetOrganizationsQueryWithReducer()

  if (!Organizations) return null
  const FundOrgs = Organizations.filter(
    (Org) => Org.type === OrganizationType.Fund,
  )
  if (!FundOrgs.length) return null

  return (
    <div id="funds" className={classes.root}>
      <Section
        title="Our funds open to you"
        description="Our fund managers allocate donations to those they believe will have the biggest impact within 
        their fund’s scope. Once you have determined which fund suits your work best, apply to it directly."
      >
        <Box>
          {FundOrgs.map((FundOrg) => {
            const Fund = Funds.find((Fund) => Fund.fields.slug === FundOrg.slug)
            const fundDescription =
              fundDescriptionMap[
                FundOrg.slug as keyof typeof fundDescriptionMap
              ]
            return (
              <Paper className={classes.fundWrapper} key={FundOrg.slug}>
                <Grid container alignItems="center">
                  <Grid
                    item
                    xs={12}
                    md={4}
                    className={classes.fundImageWrapper}
                  >
                    <ContentfulImage
                      image={Fund?.fields.bannerImage}
                      className={classes.image}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={8}
                    className={classes.fundContentWrapper}
                  >
                    <SansSerifTypography
                      variant="h5"
                      className={classes.fundName}
                      gutterBottom
                    >
                      {FundOrg.name}
                    </SansSerifTypography>
                    <SansSerifTypography
                      className={classes.fundDescription}
                      gutterBottom
                    >
                      {fundDescription.description}
                    </SansSerifTypography>
                    <SansSerifTypography className={classes.fundNote}>
                      <strong>Usually supports:</strong>&nbsp;
                      {fundDescription.usually_supports}
                    </SansSerifTypography>
                    <SansSerifTypography
                      className={classes.fundNote}
                      gutterBottom
                    >
                      <strong>Decides using:</strong>&nbsp;
                      {fundDescription.decides_using}
                    </SansSerifTypography>
                    <br />
                    {FundOrg.href && (
                      <Grid container spacing={3} justifyContent="center">
                        <Grid item sm={12} md={6}>
                          <ButtonLink
                            variant="contained"
                            href={FundOrg.href}
                            color="primary"
                            fullWidth
                          >
                            Learn more
                          </ButtonLink>
                        </Grid>
                        <Grid item sm={12} md={6}>
                          <ButtonLink
                            variant="contained"
                            href={`${FundOrg.href}#payout-reports`}
                            color="default"
                            fullWidth
                          >
                            View past grants
                          </ButtonLink>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )
          })}
        </Box>
      </Section>
    </div>
  )
}
